import { Search } from 'iconoir-react';
import { FC, useEffect, useState } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import { useMedia } from 'react-use';

import { ROLES } from '@savgroup-front-common/constants';
import { media } from '@savgroup-front-common/constants/shared';
import { safeFormattedIntlString } from '@savgroup-front-common/core/formatters';
import { useHasRole, useIsSuperAdmin } from '@savgroup-front-common/core/hooks';
import { ReversioIcon } from '@savgroup-front-common/core/protons/icons/Reversio.icon';
import { cn } from '@savgroup-front-common/primitive/lib/utils';
import { Button } from '@savgroup-front-common/primitive/ui/button';
import { SidebarProvider } from '@savgroup-front-common/primitive/ui/sidebar';
import { PRICING_PLAN, PRICING_PLAN_STATE } from '@savgroup-front-common/types';

import { ROUTES } from '@/constants/routing';
import { useAppContext } from '@/view/app/App.context';

import CreateButton from '../CreateButton/CreateButton';
import { useHeaderNewBo } from '../Header.hooks';
import Profile from '../Profile/HeaderProfile';
import { UpgradePlanButton } from '../UpgradePlanButton/UpgradePlanButton';

import { useGetMenuItems } from './AppBarMenu/hooks/useGetMenuItems';
import { AppBarMenuToggle } from './AppBarMenuToggle/AppBarMenuToggle';
import { AppBarTopNavLink } from './AppBarTopNavLink/AppBarTopNavLink';
import { BottomBar } from './BottomBar/BottomBar';
import messages from './messages';
import { NavigationDrawer } from './NavigationDrawer/NavigationDrawer';
import { AppBarSearch } from './Search/AppBarSearch/AppBarSearch';
import { AppBarSearchDrawer } from './Search/AppBarSearchDrawer/AppBarSearchDrawer';
import { AppBarSearchSheet } from './Search/AppBarSearchSheet/AppBarSearchSheet';

const TOP_NAV_ITEMS_COUNT = 3;

export const AppBar: FC = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  // The search panel is rendered as a bottom drawer on phones and as a top sheet
  // from `md` upward, where it is opened from the top bar instead of the bottom bar.
  const isMobileView = useMedia(media.maxWidth.sm);

  useEffect(() => {
    setIsSearchOpen(false);
  }, [location]);

  const handleToggleSearch = () =>
    setIsSearchOpen((wasSearchOpen) => !wasSearchOpen);

  const { menus } = useGetMenuItems();
  const topNavMenus = menus.slice(0, TOP_NAV_ITEMS_COUNT);
  const shouldDisplayDesktopMenuToggle = menus.length > TOP_NAV_ITEMS_COUNT;

  const isFromScratch = useMatch(ROUTES.FROM_SCRATCH);
  const isDemoAdmin = useHasRole(ROLES.DEMO_ADMINISTRATOR);
  const isSuperAdmin = useIsSuperAdmin();
  const { selectedSeller } = useAppContext();

  const {
    isLoading,
    isFetchingMore,
    focusedResultType,
    searchResult,
    query,
    handleChangeSearchQuery,
    handleShowMore,
  } = useHeaderNewBo();

  const shouldDisplayUpgradeButton =
    (isDemoAdmin ||
      isSuperAdmin ||
      selectedSeller?.pricingPlanState === PRICING_PLAN_STATE.CHURN) &&
    selectedSeller?.pricingPlan !== PRICING_PLAN.ENTERPRISE &&
    selectedSeller?.pricingPlan !== PRICING_PLAN.ADVANCED;

  const searchPanelProps = {
    isOpen: isSearchOpen,
    onClose: () => setIsSearchOpen(false),
    isLoading,
    isFetchingMore,
    focusedResultType,
    query,
    results: searchResult ?? [],
    onSearch: handleChangeSearchQuery,
    onShowMore: handleShowMore,
  };

  if (isFromScratch && !isSuperAdmin) {
    return (
      <SidebarProvider defaultOpen={false} className="contents">
        <header className="relative flex h-12 items-center gap-2 bg-surface-brand-default pr-3 pl-0">
          <AppBarMenuToggle
            className={cn(!shouldDisplayDesktopMenuToggle && 'md:hidden')}
          />

          <div
            className={cn(
              'flex h-full flex-1 items-center',
              !shouldDisplayDesktopMenuToggle && 'md:ml-6',
            )}
          >
            <ReversioIcon
              color="var(--color-text-contrast-default)"
              className="-translate-y-0.5"
            />
          </div>

          <Profile />

          <NavigationDrawer menus={menus} />
        </header>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider defaultOpen={false} className="contents">
      <header className="relative flex h-12 items-center gap-2 bg-surface-brand-default pr-3 pl-0">
        <AppBarMenuToggle
          className={cn(!shouldDisplayDesktopMenuToggle && 'md:hidden')}
        />

        <Link
          to={ROUTES.HOME}
          aria-label={safeFormattedIntlString(messages.goToHomePage)}
          className={cn(
            'flex h-full items-center gap-2 pr-4',
            !shouldDisplayDesktopMenuToggle && 'md:ml-6',
          )}
        >
          <ReversioIcon
            color="var(--color-text-contrast-default)"
            className="-translate-y-0.5"
          />
        </Link>

        <nav className="hidden h-full items-center gap-1 md:flex">
          {topNavMenus.map((menu) => (
            <AppBarTopNavLink key={menu.to} menu={menu} />
          ))}
        </nav>

        <div className="hidden min-w-0 flex-1 items-center justify-end gap-2 md:flex">
          {shouldDisplayUpgradeButton && <UpgradePlanButton />}

          <CreateButton />

          <Button
            size="icon-sm"
            aria-expanded={isSearchOpen}
            aria-label={
              isSearchOpen
                ? safeFormattedIntlString(messages.closeSearch)
                : safeFormattedIntlString(messages.openSearch)
            }
            data-testid="appBar_searchToggle"
            onClick={handleToggleSearch}
            className="lg:hidden"
          >
            <Search className="size-5" />
          </Button>

          <div className="hidden min-w-0 flex-1 lg:block lg:max-w-80 xl:max-w-96">
            <AppBarSearch
              isLoading={isLoading}
              isFetchingMore={isFetchingMore}
              focusedResultType={focusedResultType}
              query={query}
              results={searchResult ?? []}
              onSearch={handleChangeSearchQuery}
              onShowMore={handleShowMore}
              shouldFocusOnMount
            />
          </div>

          <Profile />
        </div>

        {isMobileView && <AppBarSearchDrawer {...searchPanelProps} />}
        {!isMobileView && <AppBarSearchSheet {...searchPanelProps} />}

        <NavigationDrawer menus={menus} />

        <BottomBar
          isSearchOpen={isSearchOpen}
          onToggleSearch={handleToggleSearch}
        />
      </header>
    </SidebarProvider>
  );
};
